<template>
  <v-container fluid class="pa-2">

    <!-- ZAGLAVLJE ------------------------------------------------------------->
    <v-card class="mb-2" outlined>
      <v-card-text class="py-2">

        <!-- red 1: sto se prikazuje -->
        <v-row dense align="center">
          <v-col cols="12" md="5">
            <v-btn-toggle v-model="opseg" mandatory dense color="primary" @change="load">
              <v-btn small value="stock">Sa stanjem</v-btn>
              <v-btn small value="move">+ promet</v-btn>
              <v-btn small value="all">Cijeli šifrarnik</v-btn>
            </v-btn-toggle>
            <span class="caption grey--text ml-2">{{ opsegOpis }}</span>
          </v-col>

          <v-col cols="6" md="2">
            <v-select
              v-model="wareHouseId"
              :items="wareHouses"
              item-text="WareHouseName"
              item-value="WareHouseId"
              label="Skladište"
              dense hide-details clearable
              @change="load"
            ></v-select>
          </v-col>

          <v-col cols="6" md="2">
            <v-select
              v-model="grupa"
              :items="grupe"
              label="Grupa"
              dense hide-details clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="trazi"
              append-icon="mdi-magnify"
              label="Traži po svemu na listi"
              dense hide-details clearable
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- red 2: razdoblje i kolone -->
        <v-row dense align="center" class="mt-1">
          <v-col cols="12" md="5">
            <span class="caption">Godina {{ businessYear }} · promet od</span>
            <input type="date" v-model="fromDate" class="mx-1" />
            <span class="caption">do</span>
            <input type="date" v-model="toDate" class="mx-1" />
            <v-btn x-small text color="primary" @click="resetPeriod">cijela godina</v-btn>
            <v-btn x-small color="primary" class="ml-1" :loading="saving" @click="load">Prikaži</v-btn>
          </v-col>

          <v-col cols="12" md="4">
            <v-btn-toggle v-model="pogled" mandatory dense color="primary" class="mr-2">
              <v-btn small value="artikli">Artikli</v-btn>
              <v-btn small value="grupe">Grupe</v-btn>
            </v-btn-toggle>
            <v-btn-toggle v-model="kolone" multiple dense color="primary" v-if="!samoVrijednost">
              <v-btn small value="promet">Promet</v-btn>
              <!-- RUC nema smisla kad je sve u prodajnim cijenama. Gumb se
                   onemogucuje, a NE uklanja: v-if bi ga odjavio iz skupine, pa
                   bi "v-btn-toggle" izbacio "zarada" iz modela i odabir RUC-a
                   bi se izgubio zauvijek - i nakon povratka na dobavne cijene. -->
              <v-btn small value="zarada" :disabled="poProdajnoj">RUC</v-btn>
            </v-btn-toggle>
            <!-- Po kojoj se cijeni vrednuju iznosi. Zbroj, PDF i Excel prate
                 isti odabir. -->
            <v-btn-toggle v-model="cijene" mandatory dense color="primary" class="ml-2"
                           v-if="!samoVrijednost" @change="load">
              <v-btn small value="nabavna">Dobavne</v-btn>
              <v-btn small value="prodajna">Bez PDV-a</v-btn>
            </v-btn-toggle>
            <!-- Drugo pitanje od ostatka izvjestaja: ne "po cemu je roba usla"
                 nego "koliko zaliha vrijedi danas". Zato iskljucuje sve iznose
                 i RUC - oni se citaju s dokumenata, a ovo iz sifrarnika. -->
            <v-btn-toggle v-model="samoVrijednost" dense color="primary" class="ml-2">
              <v-btn small :value="true">Vrijednost zalihe</v-btn>
            </v-btn-toggle>
            <v-btn x-small text class="ml-2" @click="showLegend = !showLegend">
              {{ showLegend ? 'sakrij opis' : 'što znači koja kolona' }}
            </v-btn>
          </v-col>

          <v-col cols="12" md="3" class="text-right">
            <v-checkbox
              v-model="withServices"
              label="i usluge"
              dense hide-details class="d-inline-block mt-0 mr-3"
              @change="load"
            ></v-checkbox>
            <v-btn x-small text color="primary" :disabled="!redovi.length" @click="doPdf">PDF</v-btn>
            <v-btn x-small text color="primary" :disabled="!redovi.length" @click="doXlsx">Excel</v-btn>
          </v-col>
        </v-row>

        <!-- upozorenje kad period izlazi izvan poslovne godine -->
        <v-row v-if="meta && meta.periodOutsideYear" dense>
          <v-col cols="12">
            <span class="caption orange--text text--darken-3">
              Razdoblje izlazi izvan poslovne godine {{ businessYear }}.
              Stanje se i dalje računa samo za {{ businessYear }} (inventura + promet te godine),
              a kolone prometa pokrivaju {{ localDate(meta.periodFrom) }} - {{ localDate(meta.periodTo) }}.
            </span>
          </v-col>
        </v-row>

        <!-- Opis kolona prati ono sto je na ekranu: isti redoslijed i samo one
             kolone koje su trenutno prikazane. Opis kolone koje nema zbunjuje
             vise nego da ga nema. -->
        <v-row v-if="showLegend" dense>
          <v-col cols="12">
            <span class="caption grey--text text--darken-1" v-if="samoVrijednost">
              <b>Stanje</b> - zaduženje minus razduženje unutar poslovne godine.
              <b>Cijena bez PDV-a</b> i <b>Cijena s PDV-om</b> - vrijedeće cijene iz šifrarnika
              artikala, ne cijene s dokumenata; cijena s PDV-om je cijena bez PDV-a uvećana za
              stopu upisanu na artiklu.
              <b>Vrijednost bez PDV-a</b> i <b>Vrijednost s PDV-om</b> - količina stanja × ta cijena,
              oboje zbrojeno u podnožju. To je <i>trenutna</i> vrijednost zalihe: koliko roba
              vrijedi danas, a ne po čemu je ušla. Zato se u ovom pogledu ne prikazuju iznosi
              prometa ni RUC - oni se čitaju s dokumenata, pa se te dvije stvari ne smiju
              čitati kao jedna.
            </span>

            <span class="caption grey--text text--darken-1" v-else>
              <b>Poč. stanje</b> - zaliha na prvi dan razdoblja: promet od početka
              poslovne godine do dana prije. Za razdoblje od 01.01. je nula, jer prije
              toga nema ničega.
              <b>Ulaz kol. / Izlaz kol.</b> - promet u odabranom razdoblju, prvi i zadnji dan
              uključeni; inventura koja padne u razdoblje broji se u ulaz.
              <b>Stanje</b> - zaduženje minus razduženje unutar poslovne godine;
              poč. stanje + ulaz − izlaz daje zalihu na kraju razdoblja.

              Vrijednosti stoje istim redom kao količine iznad njih.
              <b>{{ nazivUlazVr }}</b> - ulazna količina ×
              {{ poProdajnoj ? 'ostvarena prodajna cijena bez PDV-a' : 'nabavna cijena' }}.
              <template v-if="poProdajnoj">
                <b>{{ nazivIzlazVr }}</b> - izlazna količina × ostvarena prodajna cijena bez PDV-a.
              </template>
              <template v-if="prikaziZaradu">
                <b>Izlazna dobavna vrijednost</b> - nabavna vrijednost izlaza, po cijeni upisanoj
                na samom dokumentu. To je kolona koja zatvara račun ulaz − izlaz = stanje, jer je
                jedina u istom mjerilu kao ulazna.
              </template>
              <b>{{ nazivStanjeVr }}</b> - količina stanja × {{ nazivProsjCijena }}, financijska
              vrijednost zalihe.
              <template v-if="!poProdajnoj">
                <b>Izlaz vr.</b> - <i>prodajna</i> vrijednost izlaza bez PDV-a, umanjena za rabat.
                Nije u istom mjerilu kao ulazna i izlazna dobavna vrijednost, pa stoji iza stanja,
                a ne među njima.
              </template>
              <b>{{ nazivProsjCijena }}</b> - (vrijednost početnog stanja + vrijednost ulaza) /
              (njihova količina); računa se nad odabranim razdobljem, pa se s promjenom razdoblja
              mijenja i vrijednost zalihe, dok se količina stanja ne mijenja.

              <template v-if="prikaziZaradu">
                <b>RUC</b> - ostvarena razlika u cijeni: <i>Izlaz vr. − Izlazna dobavna vrijednost</i>.
                <b>Nab. (prosj.)</b> - izlazna količina × {{ nazivProsjCijena }}, nabava po prosjeku
                umjesto po cijeni s dokumenta. <b>RUC (prosj.)</b> je RUC računat s njom - koristi se
                kad nabavna cijena nije upisana na izlazu.
                <b>RUC %</b> je RUC podijeljen s <i>nabavnom</i> vrijednošću izlaza, ista osnovica
                koju koristi i marža na primci; crtica znači da nema nabave za dijeliti.
              </template>

              <template v-if="!poProdajnoj">
                Nabavne vrijednosti se računaju iz <i>itemSuppPrice</i> × količina - na primci
                <i>itemPrice</i> je već prodajna cijena, pa bi davala uvećanu nabavu.
              </template>
              <b>Dobavne / Bez PDV-a</b> - po kojoj se cijeni vrednuju početno stanje, ulaz,
              prosječna cijena i zaliha. Iznosi govore koliko je robe prošlo po kojoj cijeni, pa je
              prodajna cijena <i>ostvarena</i>, umanjena za rabat - iznos retka podijeljen s
              količinom, a ne cjenovnik. U osnovici „Bez PDV-a" nema RUC-a ni izlazne dobavne
              vrijednosti: RUC je razlika prodajne i nabavne, pa bi ondje bio nula u svakom retku,
              a nabavna vrijednost izlaza čita se samo uz njega.
              <b>Vrijednost zalihe</b> - zaseban pogled: koliko zaliha vrijedi <i>danas</i>, po
              cijenama iz šifrarnika artikala.
            </span>
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>

    <!-- LISTA ----------------------------------------------------------------->
    <v-card outlined>
      <v-data-table
        dense
        :headers="headers"
        :items="tablicaRedovi"
        :loading="saving"
        :item-key="prikazGrupa ? 'kljuc' : 'itemID'"
        :items-per-page="50"
        :footer-props="{'items-per-page-options': [25, 50, 100, 200, -1]}"
        no-data-text="Nema artikala za odabrane uvjete."
        loading-text="Učitavam..."
        @click:row="onRowClick"
        class="row-pointer stock-table"
      >
        <template v-slot:[`item.itemName`]="{ item }">
          <span :class="{'grey--text': item.itemNotActiv}">{{ item.itemName }}</span>
          <v-icon v-if="item.itemService" x-small class="ml-1" title="usluga">mdi-wrench</v-icon>
          <v-icon v-if="!item.uSifrarniku" x-small color="orange" class="ml-1"
            title="artikla nema u šifrarniku">mdi-alert</v-icon>
        </template>
        <template v-slot:[`item.stanjeKol`]="{ item }">
          <span :class="item.stanjeKol < 0 ? 'red--text font-weight-bold' : ''">{{ kol(item.stanjeKol) }}</span>
        </template>
        <template v-slot:[`item.pocKol`]="{ item }">{{ kol(item.pocKol) }}</template>
        <template v-slot:[`item.ulazKol`]="{ item }">{{ kol(item.ulazKol) }}</template>
        <template v-slot:[`item.izlazKol`]="{ item }">{{ kol(item.izlazKol) }}</template>
        <template v-slot:[`item.avgNC`]="{ item }">{{ novac(item.avgNC) }}</template>
        <template v-slot:[`item.stanjeVr`]="{ item }">{{ novac(item.stanjeVr) }}</template>
        <template v-slot:[`item.ulazVr`]="{ item }">{{ novac(item.ulazVr) }}</template>
        <template v-slot:[`item.izlazVr`]="{ item }">{{ novac(item.izlazVr) }}</template>
        <template v-slot:[`item.izlazNabVr`]="{ item }">{{ novac(item.izlazNabVr) }}</template>
        <template v-slot:[`item.izlazNabVrProsj`]="{ item }">{{ novac(item.izlazNabVrProsj) }}</template>
        <template v-slot:[`item.zaradaUpisana`]="{ item }">
          <span :class="zaradaClass(item.zaradaUpisana)">{{ novac(item.zaradaUpisana) }}</span>
          <v-icon v-if="nepotpunaNC(item)" x-small color="orange" class="ml-1"
            title="dio izlaza nema upisanu nabavnu cijenu">mdi-alert</v-icon>
        </template>
        <template v-slot:[`item.zaradaProsj`]="{ item }">
          <span :class="zaradaClass(item.zaradaProsj)">{{ novac(item.zaradaProsj) }}</span>
        </template>
        <template v-slot:[`item.rucPosto`]="{ item }">
          <span :class="zaradaClass(item.rucPosto)">{{ posto(item.rucPosto) }}</span>
        </template>
        <template v-slot:[`item.rucPostoProsj`]="{ item }">
          <span :class="zaradaClass(item.rucPostoProsj)">{{ posto(item.rucPostoProsj) }}</span>
        </template>

        <!-- The totals row is built from the same headers array as the table, so it
             can never drift out of alignment when columns are switched on or off. -->
        <template v-slot:[`body.append`]>
          <tr class="font-weight-bold grey lighten-4">
            <td v-for="(c, idx) in zbrojCelije" :key="idx"
              :class="c.align === 'end' ? 'text-right' : ''">{{ c.text }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- KARTICA ARTIKLA - dijeli se s DocumentItemCardBySupp -------------->
    <WareHouseItemCard ref="kartica" />

    <v-snackbar v-model="snackbar" bottom :timeout="timeout" :color="color">{{ text }}</v-snackbar>
  </v-container>
</template>

<script>
// Stanje skladista i promet po artiklima.
//
// The server sends one aggregated row per article - for the biggest company in the
// database that is about 1300 rows - so search, filter, sort and the totals all run
// here without going back for more. Only changing the period, the warehouse, the
// scope or the services checkbox costs a request.
//
// Two windows are in play and they are deliberately not the same:
//   stanje  always the business year from the store, because every year opens with
//           an inventura that books the whole stock as zaduzenje
//   promet  the period the user picked, which may be several years wide
// See WareHouseStateController for why.

import WareHouseStockService from '@/services/WareHouseStockService'
import WareHouseMarkService from '@/services/WareHouseMarkService'
import documentLocalName from '@/snippets/documentLocalName'
import {printStockPdf} from './wareHouseStockPdf'
import WareHouseItemCard from './WareHouseItemCard'
import dayjs from 'dayjs'
import xlsx from 'json-as-xlsx'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

// Lowercase and strip the Croatian diacritics, so "sifra" finds "šifra" and the
// search does not depend on how the article was typed in.
// The bucket articles land in when they carry no group at all. It doubles as the
// value of the Grupa filter, so clicking that row on the group view really does
// narrow the list down to the unfiled articles.
const BEZ_GRUPE = '(bez grupe)'

const fold = (v) => String(v === null || v === undefined ? '' : v)
  .toLowerCase()
  .replace(/[čć]/g, 'c')
  .replace(/ž/g, 'z')
  .replace(/š/g, 's')
  .replace(/đ/g, 'd')

export default {
  name: 'warehousestock',
  components: {WareHouseItemCard},
  data () {
    return {
      saving: false,
      snackbar: false,
      text: '',
      color: 'red',
      timeout: 4000,

      opseg: 'stock',
      pogled: 'artikli',
      kolone: ['promet', 'zarada'],
      // Osnovica za iznose: 'nabavna' (dobavne cijene) ili 'prodajna'
      // (prodajne cijene bez PDV-a).
      cijene: 'nabavna',
      // Prikaz trenutne vrijednosti zalihe po vazecim cijenama iz sifrarnika.
      // Nije dodatna kolona nego drugi pogled - iznosi s dokumenata i RUC se
      // pritom ne prikazuju, da se dvije razlicite stvari ne citaju kao jedna.
      samoVrijednost: false,
      withServices: false,
      wareHouseId: null,
      wareHouses: [],
      grupa: null,
      trazi: '',
      fromDate: null,
      toDate: null,
      showLegend: false,

      stavke: [],
      meta: null
    }
  },
  mounted () {
    this.resetPeriod()
    this.wareHouseId = this.$store.state.warehousemarkid ? this.$store.state.warehousemarkid : null
    this.loadWareHouses()
    this.load()
  },
  computed: {
    businessYear () {
      return this.$store.state.businessYear
    },
    companyid () {
      return this.$store.state.companyid
    },
    prikaziPromet () {
      return !this.samoVrijednost && this.kolone.indexOf('promet') !== -1
    },
    prikaziZaradu () {
      // RUC je razlika prodajne i nabavne. Kad se sve vrednuje prodajnim
      // cijenama, nabavne strane nema, pa bi RUC bio nula u svakom retku -
      // kolona koja uvijek pokazuje nulu gora je od kolone koje nema.
      return !this.samoVrijednost && !this.poProdajnoj && this.kolone.indexOf('zarada') !== -1
    },
    prikazGrupa () {
      return this.pogled === 'grupe'
    },

    // Nazivi kolona koje ovise o odabranoj osnovici. Drze se na jednom mjestu
    // da ekran, PDF i Excel ne mogu razici - pise li na ekranu "dobavne", a u
    // Excelu "bez PDV-a", ispis se ne moze provjeriti.
    poProdajnoj () {
      return this.cijene === 'prodajna'
    },
    nazivUlazVr () {
      return this.poProdajnoj ? 'Ulazna vrijednost bez PDV-a' : 'Ulazna dobavna vrijednost'
    },
    nazivStanjeVr () {
      return this.poProdajnoj ? 'Stanje po cijeni bez PDV-a' : 'Stanje po dobavnoj cijeni'
    },
    nazivProsjCijena () {
      return this.poProdajnoj ? 'Prosj. PC' : 'Prosj. NC'
    },
    // "Izlaz vr." je oduvijek prodajna vrijednost izlaza bez PDV-a. U prodajnoj
    // osnovici to je upravo kolona koja se trazi, pa dobiva puni naziv; kolona
    // "Izlazna dobavna vrijednost" tada ne postoji jer s njom odlazi i RUC.
    nazivIzlazVr () {
      return this.poProdajnoj ? 'Izlazna vrijednost bez PDV-a' : 'Izlaz vr.'
    },
    // Jedna recenica za zaglavlje ispisa i za Excel.
    osnovicaOpis () {
      if (this.samoVrijednost) {
        return 'Trenutna vrijednost zalihe po cijenama iz šifrarnika artikala'
      }
      return this.poProdajnoj
        ? 'Iznosi su po ostvarenim prodajnim cijenama bez PDV-a (umanjenim za rabat)'
        : 'Iznosi su po dobavnim cijenama'
    },
    // What the table is fed: either the articles or one row per group.
    tablicaRedovi () {
      return this.prikazGrupa ? this.grupeRedovi : this.redovi
    },
    /**
     * One row per group, built from the rows already on screen.
     *
     * Only VALUES are summed. Quantities are deliberately left out: an article in
     * kom and one in metrima do not add up to anything, so a "total quantity" per
     * group would be a number that looks right and means nothing. What is counted
     * instead is how many articles the group holds.
     *
     * Groups are matched case insensitively and without the surrounding spaces, so
     * "ROBA" and "roba" are one group; the spelling used by most articles is what
     * gets printed.
     */
    grupeRedovi () {
      const map = {}
      this.redovi.forEach(i => {
        const izvorno = String(i.GroupName || i.itemGroup || '').trim()
        const kljuc = fold(izvorno) || BEZ_GRUPE
        if (!map[kljuc]) {
          map[kljuc] = {
            kljuc: kljuc,
            nazivi: {},
            grupa: izvorno || BEZ_GRUPE,
            artikala: 0,
            saStanjem: 0,
            stanjeVr: 0,
            ulazVr: 0,
            izlazVr: 0,
            izlazNabVr: 0,
            izlazNabVrProsj: 0,
            zaradaUpisana: 0,
            zaradaProsj: 0,
            zalihaBezPdv: 0,
            zalihaSPdv: 0
          }
        }
        const g = map[kljuc]
        if (izvorno) { g.nazivi[izvorno] = (g.nazivi[izvorno] || 0) + 1 }
        g.artikala += 1
        if (num(i.stanjeKol) !== 0) { g.saStanjem += 1 }
        g.stanjeVr += num(i.stanjeVr)
        g.ulazVr += num(i.ulazVr)
        g.izlazVr += num(i.izlazVr)
        g.izlazNabVr += num(i.izlazNabVr)
        g.izlazNabVrProsj += num(i.izlazNabVrProsj)
        g.zaradaUpisana += num(i.zaradaUpisana)
        g.zaradaProsj += num(i.zaradaProsj)
        g.zalihaBezPdv += num(i.zalihaBezPdv)
        g.zalihaSPdv += num(i.zalihaSPdv)
      })

      const r2 = (v) => Math.round(v * 100) / 100
      return Object.keys(map).map(k => {
        const g = map[k]
        // the spelling most articles use wins over "(bez grupe)"
        const nazivi = Object.keys(g.nazivi)
        if (nazivi.length) {
          nazivi.sort((a, b) => g.nazivi[b] - g.nazivi[a] || a.localeCompare(b, 'hr'))
          g.grupa = nazivi[0]
        }
        delete g.nazivi
        ('stanjeVr ulazVr izlazVr izlazNabVr izlazNabVrProsj zaradaUpisana zaradaProsj ' +
         'zalihaBezPdv zalihaSPdv')
          .split(' ').forEach(f => { g[f] = r2(g[f]) })
        g.rucPosto = g.izlazNabVr > 0 ? r2((g.zaradaUpisana / g.izlazNabVr) * 100) : null
        g.rucPostoProsj = g.izlazNabVrProsj > 0 ? r2((g.zaradaProsj / g.izlazNabVrProsj) * 100) : null
        return g
      }).sort((a, b) => a.grupa.localeCompare(b.grupa, 'hr'))
    },
    opsegOpis () {
      if (this.opseg === 'stock') { return 'samo artikli sa stanjem' }
      if (this.opseg === 'move') { return 'stanje ili promet u razdoblju' }
      return 'svi artikli iz šifrarnika'
    },
    // Both the group filter and the free text search run here rather than inside
    // v-data-table, so the totals, the Excel and the pdf show exactly the rows that
    // are on screen. Letting the table filter on its own left the totals counting
    // rows the search had already hidden.
    redovi () {
      let rows = this.stavke
      if (this.grupa === BEZ_GRUPE) {
        rows = rows.filter(i => String(i.GroupName || i.itemGroup || '').trim() === '')
      } else if (this.grupa) {
        // matched the same way groups are merged, so picking "ROBA" also brings the
        // articles filed under "roba"
        const trazena = fold(this.grupa)
        rows = rows.filter(i => fold(String(i.GroupName || i.itemGroup || '').trim()) === trazena)
      }
      const tokens = this.trazilica
      if (tokens.length) {
        rows = rows.filter(i => {
          const hay = this.haystack(i)
          return tokens.every(t => hay.indexOf(t) !== -1)
        })
      }
      return rows
    },
    // Every word has to be found somewhere in the row, in any order.
    trazilica () {
      return fold(this.trazi).split(/\s+/).filter(t => t.length > 0)
    },
    // Dropdown items, built from the unfiltered list so the choice never depends on
    // what is currently picked. Spellings that differ only in case collapse into the
    // one most articles use.
    grupe () {
      const map = {}
      this.stavke.forEach(i => {
        const g = String(i.GroupName || i.itemGroup || '').trim()
        if (!g) { return }
        const k = fold(g)
        if (!map[k]) { map[k] = {} }
        map[k][g] = (map[k][g] || 0) + 1
      })
      const imena = Object.keys(map).map(k => {
        const nazivi = Object.keys(map[k])
        nazivi.sort((a, b) => map[k][b] - map[k][a] || a.localeCompare(b, 'hr'))
        return nazivi[0]
      }).sort((a, b) => a.localeCompare(b, 'hr'))
      // offered only when there is something to find under it
      if (this.stavke.some(i => String(i.GroupName || i.itemGroup || '').trim() === '')) {
        imena.push(BEZ_GRUPE)
      }
      return imena
    },
    // Totals follow the filter, so they always match what is on screen.
    zbroj () {
      const t = {
        stanjeVr: 0, ulazKol: 0, ulazVr: 0, izlazKol: 0, izlazVr: 0,
        izlazNabVr: 0, izlazNabVrProsj: 0, zaradaUpisana: 0, zaradaProsj: 0,
        zalihaBezPdv: 0, zalihaSPdv: 0
      }
      this.redovi.forEach(i => {
        Object.keys(t).forEach(k => { t[k] += num(i[k]) })
      })
      Object.keys(t).forEach(k => { t[k] = Math.round(t[k] * 100) / 100 })
      // the total percentage comes from the totals, not from averaging the per-row
      // percentages - an average of percentages weights a one euro article the same
      // as a ten thousand euro one
      t.rucPosto = t.izlazNabVr > 0 ? Math.round((t.zaradaUpisana / t.izlazNabVr) * 10000) / 100 : null
      t.rucPostoProsj = t.izlazNabVrProsj > 0 ? Math.round((t.zaradaProsj / t.izlazNabVrProsj) * 10000) / 100 : null
      // counted here so the screen, the pdf and the excel all read the same numbers
      t.artikala = this.redovi.length
      t.saStanjem = this.redovi.filter(i => num(i.stanjeKol) !== 0).length
      return t
    },
    headers () {
      // Stupci idu onako kako se racuna: prvo sve KOLICINE
      // (poc. stanje -> ulaz -> izlaz -> stanje), pa tek onda VRIJEDNOSTI.
      // Tako se redak cita slijeva nadesno kao sam racun, a stanje stoji na
      // kraju zbroja umjesto u sredini.
      //
      // In the group view the quantity columns are dropped - see grupeRedovi for why
      // mixed units cannot be added up - and the article counts take their place.
      if (this.prikazGrupa) {
        const g = [
          {text: 'Grupa', value: 'grupa', width: '260px'},
          {text: 'Artikala', value: 'artikala', align: 'end', width: '95px'},
          {text: 'Sa stanjem', value: 'saStanjem', align: 'end', width: '105px'}
        ]
        if (this.samoVrijednost) {
          g.push(
            {text: 'Vrijednost bez PDV-a', value: 'zalihaBezPdv', align: 'end', width: '140px'},
            {text: 'Vrijednost s PDV-om', value: 'zalihaSPdv', align: 'end', width: '140px'}
          )
          return g
        }
        if (this.prikaziPromet) {
          g.push({text: this.nazivUlazVr, value: 'ulazVr', align: 'end', width: '130px'})
          if (this.poProdajnoj) {
            g.push({text: this.nazivIzlazVr, value: 'izlazVr', align: 'end', width: '140px'})
          }
        }
        g.push({text: this.nazivStanjeVr, value: 'stanjeVr', align: 'end', width: '130px'})
        if (this.prikaziPromet && !this.poProdajnoj) {
          g.push({text: this.nazivIzlazVr, value: 'izlazVr', align: 'end', width: '140px'})
        }
        return g
      }

      const h = [
        {text: 'Šifra', value: 'itemID', width: '110px'},
        // grupa gave 66px back to naziv - group names are short, article names are not
        {text: 'Naziv', value: 'itemName', width: '276px'},
        {text: 'JM', value: 'itemUnit', width: '55px'},
        {text: 'Grupa', value: 'GroupName', width: '124px'}
      ]

      // --- kolicine ---
      h.push({text: 'Poč. stanje', value: 'pocKol', align: 'end', width: '95px'})
      if (this.prikaziPromet) {
        h.push(
          {text: 'Ulaz kol.', value: 'ulazKol', align: 'end', width: '95px'},
          {text: 'Izlaz kol.', value: 'izlazKol', align: 'end', width: '95px'}
        )
      }
      h.push({text: 'Stanje', value: 'stanjeKol', align: 'end', width: '90px'})

      // Trenutna vrijednost zalihe: stanje puta cijena iz sifrarnika. Uz svaku
      // vrijednost stoji i sama cijena - bez nje se ne vidi zasto redak iznosi
      // toliko, a kriva cijena u sifrarniku se ne moze uociti.
      if (this.samoVrijednost) {
        h.push(
          {text: 'Cijena bez PDV-a', value: 'katPC', align: 'end', width: '120px'},
          {text: 'Vrijednost bez PDV-a', value: 'zalihaBezPdv', align: 'end', width: '140px'},
          {text: 'Cijena s PDV-om', value: 'katPCsPdv', align: 'end', width: '120px'},
          {text: 'Vrijednost s PDV-om', value: 'zalihaSPdv', align: 'end', width: '140px'}
        )
        return h
      }

      // --- vrijednosti ---
      if (this.prikaziPromet) {
        // nabavna vrijednost ulaza (kolicina x nabavna cijena), za razliku od
        // "Izlaz vr.", koji je PRODAJNA vrijednost bez PDV-a
        // Vrijednosti idu istim redom kao kolicine iznad njih: ulaz, izlaz,
        // stanje. Koja je kolona "izlaz" ovisi o osnovici:
        //
        //   bez PDV-a   "Izlazna vrijednost bez PDV-a" (ista kolona, drugi naziv)
        //   dobavne     "Izlazna dobavna vrijednost" - jedino se s njom zatvara
        //               racun ulaz - izlaz = stanje, jer je u istom mjerilu
        //
        // "Izlaz vr." je PRODAJNA vrijednost izlaza; u dobavnoj osnovici zato
        // stoji tek iza stanja, kao podatak za sebe i kao ulaz u RUC.
        h.push({text: this.nazivUlazVr, value: 'ulazVr', align: 'end', width: '130px'})
        if (this.poProdajnoj) {
          h.push({text: this.nazivIzlazVr, value: 'izlazVr', align: 'end', width: '140px'})
        }
      }
      if (this.prikaziZaradu) {
        // par "Ulaznoj dobavnoj vrijednosti": nabavna vrijednost onoga sto je
        // izaslo. Stoji uz nju, a ne medu RUC stupcima, jer se to dvoje cita
        // zajedno. "Izlaz vr." je prodajna, pa se te dvije ne smiju brkati.
        h.push({text: 'Izlazna dobavna vrijednost', value: 'izlazNabVr', align: 'end', width: '130px'})
      }
      // stanjeKol x prosjecna cijena - financijska vrijednost zalihe
      h.push({text: this.nazivStanjeVr, value: 'stanjeVr', align: 'end', width: '130px'})
      if (this.prikaziPromet && !this.poProdajnoj) {
        h.push({text: this.nazivIzlazVr, value: 'izlazVr', align: 'end', width: '140px'})
      }
      h.push({text: this.nazivProsjCijena, value: 'avgCijena', align: 'end', width: '95px'})
      // Izlaz vr. (prodajna) stoji neposredno ispred RUC-a, jer se RUC iz nje i
      // cita: RUC = Izlaz vr. - Izlazna dobavna vrijednost.

      if (this.prikaziZaradu) {
        h.push(
          // RUC = ostvarena razlika u cijeni = izlazVr - nabavna vrijednost izlaza,
          // u dvije varijante jer je nabavna cijena upisana na samo pola izlaza
          {text: 'RUC', value: 'zaradaUpisana', align: 'end', width: '100px'},
          // wide enough for a three digit percentage - 177,78 % wrapped at 80px
          {text: 'RUC %', value: 'rucPosto', align: 'end', width: '105px'},
          {text: 'Nab. (prosj.)', value: 'izlazNabVrProsj', align: 'end', width: '105px'},
          {text: 'RUC (prosj.)', value: 'zaradaProsj', align: 'end', width: '105px'},
          {text: 'RUC % (prosj.)', value: 'rucPostoProsj', align: 'end', width: '105px'}
        )
      }
      return h
    },
    // One cell per header, so the totals row lines up with whatever columns are on.
    // Per unit and per quantity columns are left blank: an average price and a sum
    // of mixed units are not things that can be totalled.
    zbrojCelije () {
      const t = this.zbroj
      const novac = (v) => this.novac(v)
      const vrijednosti = {
        stanjeVr: novac(t.stanjeVr),
        ulazVr: novac(t.ulazVr),
        izlazVr: novac(t.izlazVr),
        izlazNabVr: novac(t.izlazNabVr),
        izlazNabVrProsj: novac(t.izlazNabVrProsj),
        zaradaUpisana: novac(t.zaradaUpisana),
        zaradaProsj: novac(t.zaradaProsj),
        rucPosto: this.posto(t.rucPosto),
        rucPostoProsj: this.posto(t.rucPostoProsj),
        ulazKol: this.kol(t.ulazKol),
        izlazKol: this.kol(t.izlazKol),
        artikala: String(t.artikala),
        saStanjem: String(t.saStanjem),
        zalihaBezPdv: novac(t.zalihaBezPdv),
        zalihaSPdv: novac(t.zalihaSPdv)
        // katPC i katPCsPdv namjerno nisu u zbroju: cijene se ne zbrajaju.
      }
      return this.headers.map((h, index) => {
        if (index === 0) { return {text: 'UKUPNO', align: h.align} }
        if (!this.prikazGrupa && index === 1) {
          return {text: this.redovi.length + ' artikala', align: h.align}
        }
        return {text: vrijednosti[h.value] !== undefined ? vrijednosti[h.value] : '', align: h.align}
      })
    }
  },
  methods: {
    // Text and numbers of one row, folded, so the search covers everything that is
    // printed in the list - sifra, naziv, grupa, kolicine and iznosi alike.
    haystack (i) {
      if (i.__hay === undefined) {
        i.__hay = fold([
          i.itemID, i.itemName, i.itemUnit, i.itemGroup, i.GroupName,
          i.pocKol, i.stanjeKol, i.stanjeVr, i.avgNC,
          i.ulazKol, i.ulazVr, i.izlazKol, i.izlazVr,
          i.izlazNabVr, i.zaradaUpisana, i.izlazNabVrProsj, i.zaradaProsj
        ].join(' '))
      }
      return i.__hay
    },
    kol (v) {
      const n = num(v)
      // quantities are rarely fractional, so whole numbers stay whole
      const dec = Math.abs(n - Math.round(n)) < 0.0001 ? 0 : 2
      return n.toFixed(dec).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    novac (v) {
      return num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    // null means there was no nabava to divide by - a dash, not 0 %
    posto (v) {
      if (v === null || v === undefined) { return '–' }
      return num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' %'
    },
    localDate (d) {
      return d ? dayjs(d).format('DD.MM.YYYY') : ''
    },
    docName (n) {
      return n ? documentLocalName(n) : ''
    },
    zaradaClass (v) {
      const n = num(v)
      if (n < 0) { return 'red--text' }
      if (n > 0) { return 'green--text text--darken-2' }
      return ''
    },
    // An article whose issued quantity is only partly priced makes the "upisana"
    // margin look better or worse than it is.
    nepotpunaNC (item) {
      return num(item.izlazKol) > 0 && num(item.izlazKolSNC) + 0.0001 < num(item.izlazKol)
    },
    resetPeriod () {
      this.fromDate = this.businessYear + '-01-01'
      this.toDate = this.businessYear + '-12-31'
    },
    notify (msg, col) {
      this.text = msg
      this.color = col || 'red'
      this.snackbar = true
    },

    // Documents carry WareHouseMarks.WareHouseId (1, 2 ...), not the table id.
    async loadWareHouses () {
      try {
        const {data} = await WareHouseMarkService.index({CompanyId: this.companyid})
        this.wareHouses = data && data.wareHouseMarks ? data.wareHouseMarks : []
      } catch (err) {
        // the list still works without it, the warehouse filter just stays empty
        this.wareHouses = []
      }
    },

    async load () {
      if (!this.companyid) {
        this.notify('Nije odabrana tvrtka.')
        return
      }
      this.saving = true
      try {
        const {data} = await WareHouseStockService.stock({
          CompanyId: this.companyid,
          BusinessYear: this.businessYear,
          WareHouseId: this.wareHouseId || undefined,
          fromDate: this.fromDate,
          toDate: this.toDate,
          mode: this.opseg,
          withServices: this.withServices ? 'true' : 'false',
          cijene: this.cijene
        })
        this.stavke = data && data.items ? data.items : []
        this.meta = data && data.meta ? data.meta : null
        if (this.stavke.length === 0) {
          this.notify('Nema artikala za odabrane uvjete.', 'orange darken-2')
        }
      } catch (err) {
        this.stavke = []
        this.meta = null
        this.notify('Greška pri dohvatu: ' + (err && err.message ? err.message : err))
      }
      this.saving = false
    },

    // In the group view a click drills into the group instead of opening a card:
    // it sets the group filter and switches back to the articles.
    onRowClick (row) {
      if (!row) { return }
      if (this.prikazGrupa) {
        this.grupa = row.grupa
        this.pogled = 'artikli'
        return
      }
      this.openCard(row)
    },

    openCard (item) {
      if (!item) { return }
      this.$refs.kartica.open({
        itemID: item.itemID,
        itemName: item.itemName,
        itemUnit: item.itemUnit,
        itemGroup: item.itemGroup,
        GroupName: item.GroupName,
        avgNC: item.avgNC,
        WareHouseId: this.wareHouseId || undefined,
        fromDate: this.fromDate,
        toDate: this.toDate
      })
    },

    // The pdf gets the same rows, totals and column groups the list is showing.
    doPdf () {
      if (!this.redovi.length) {
        this.notify('Nema redaka za ispis.', 'orange darken-2')
        return
      }
      const wh = this.wareHouses.find(w => w.WareHouseId === this.wareHouseId)
      printStockPdf({
        items: this.tablicaRedovi,
        grupe: this.prikazGrupa,
        totals: this.zbroj,
        ukupnoArtikala: this.redovi.length,
        company: this.$store.state.companyActiv ? this.$store.state.companyActiv : {},
        businessYear: this.businessYear,
        fromDate: this.fromDate,
        toDate: this.toDate,
        promet: this.prikaziPromet,
        zarada: this.prikaziZaradu,
        opsegOpis: this.opsegOpis,
        osnovicaOpis: this.osnovicaOpis,
        samoVrijednost: this.samoVrijednost,
        nazivUlazVr: this.nazivUlazVr,
        nazivStanjeVr: this.nazivStanjeVr,
        nazivProsjCijena: this.nazivProsjCijena,
        nazivIzlazVr: this.nazivIzlazVr,
        poProdajnoj: this.poProdajnoj,
        wareHouseName: wh ? wh.WareHouseName : '',
        grupa: this.grupa,
        trazi: this.trazi,
        withServices: this.withServices
      })
    },

    doXlsx () {
      const comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}
      const lines = []
      lines.push({Naziv: comp.CompanyName || ''})
      lines.push({Naziv: 'Stanje skladišta i promet po artiklima'})
      lines.push({Naziv: 'Poslovna godina ' + this.businessYear +
        ', promet ' + this.localDate(this.fromDate) + ' - ' + this.localDate(this.toDate)})
      lines.push({Naziv: this.osnovicaOpis})
      if (this.grupa) { lines.push({Naziv: 'Grupa: ' + this.grupa}) }
      lines.push({Naziv: 'Datum ispisa: ' + dayjs().format('DD.MM.YYYY HH:mm')})
      lines.push({})

      if (this.prikazGrupa) {
        this.grupeRedovi.forEach(g => lines.push({
          Grupa: g.grupa,
          Artikala: g.artikala,
          SaStanjem: g.saStanjem,
          VrijZalihe: g.stanjeVr,
          ZalihaBezPdv: g.zalihaBezPdv,
          ZalihaSPdv: g.zalihaSPdv,
          UlazVr: g.ulazVr,
          IzlazVr: g.izlazVr,
          NabIzlaza: g.izlazNabVr,
          Zarada: g.zaradaUpisana,
          RucPosto: g.rucPosto,
          NabIzlazaProsj: g.izlazNabVrProsj,
          ZaradaProsj: g.zaradaProsj,
          RucPostoProsj: g.rucPostoProsj
        }))
      } else {
      this.redovi.forEach(i => lines.push({
        Sifra: i.itemID,
        Naziv: i.itemName,
        JM: i.itemUnit,
        Grupa: i.GroupName || i.itemGroup,
        PocKol: i.pocKol,
        Stanje: i.stanjeKol,
        VrijZalihe: i.stanjeVr,
        ProsjNC: i.avgCijena,
        CijenaBezPdv: i.katPC,
        ZalihaBezPdv: i.zalihaBezPdv,
        CijenaSPdv: i.katPCsPdv,
        ZalihaSPdv: i.zalihaSPdv,
        UlazKol: i.ulazKol,
        UlazVr: i.ulazVr,
        IzlazKol: i.izlazKol,
        IzlazVr: i.izlazVr,
        NabIzlaza: i.izlazNabVr,
        Zarada: i.zaradaUpisana,
        RucPosto: i.rucPosto,
        NabIzlazaProsj: i.izlazNabVrProsj,
        ZaradaProsj: i.zaradaProsj,
        RucPostoProsj: i.rucPostoProsj
      }))
      }

      const z = this.zbroj
      lines.push(this.prikazGrupa
        ? {
          Grupa: 'UKUPNO (' + this.grupeRedovi.length + ' grupa, ' + this.redovi.length + ' artikala)',
          Artikala: this.redovi.length,
          VrijZalihe: z.stanjeVr, ZalihaBezPdv: z.zalihaBezPdv, ZalihaSPdv: z.zalihaSPdv,
          UlazVr: z.ulazVr, IzlazVr: z.izlazVr,
          NabIzlaza: z.izlazNabVr, Zarada: z.zaradaUpisana, RucPosto: z.rucPosto,
          NabIzlazaProsj: z.izlazNabVrProsj, ZaradaProsj: z.zaradaProsj, RucPostoProsj: z.rucPostoProsj
        }
        : {
          Sifra: '', Naziv: 'UKUPNO (' + this.redovi.length + ' artikala)',
          VrijZalihe: z.stanjeVr, ZalihaBezPdv: z.zalihaBezPdv, ZalihaSPdv: z.zalihaSPdv,
          UlazKol: z.ulazKol, UlazVr: z.ulazVr,
          IzlazKol: z.izlazKol, IzlazVr: z.izlazVr, NabIzlaza: z.izlazNabVr,
          Zarada: z.zaradaUpisana, RucPosto: z.rucPosto,
          NabIzlazaProsj: z.izlazNabVrProsj, ZaradaProsj: z.zaradaProsj, RucPostoProsj: z.rucPostoProsj
        })

      const kol = {format: '#,##0.00'}
      const eur = {format: '€#,##0.00'}
      // the group sheet drops sifra/naziv/JM and the quantity columns, which cannot
      // be added across articles with different units
      // U pogledu trenutne vrijednosti izlaze samo kolicina i dvije vrijednosti -
      // isto kao na ekranu i u pdf-u.
      const columns = this.samoVrijednost
        ? (this.prikazGrupa
          ? [
            {label: 'Grupa', value: 'Grupa'},
            {label: 'Artikala', value: 'Artikala'},
            {label: 'Sa stanjem', value: 'SaStanjem'},
            {label: 'Vrijednost bez PDV-a', value: 'ZalihaBezPdv', ...eur},
            {label: 'Vrijednost s PDV-om', value: 'ZalihaSPdv', ...eur}
          ]
          : [
            {label: 'Šifra', value: 'Sifra'},
            {label: 'Naziv', value: 'Naziv'},
            {label: 'JM', value: 'JM'},
            {label: 'Grupa', value: 'Grupa'},
            {label: 'Stanje', value: 'Stanje', ...kol},
            {label: 'Cijena bez PDV-a', value: 'CijenaBezPdv', ...eur},
            {label: 'Vrijednost bez PDV-a', value: 'ZalihaBezPdv', ...eur},
            {label: 'Cijena s PDV-om', value: 'CijenaSPdv', ...eur},
            {label: 'Vrijednost s PDV-om', value: 'ZalihaSPdv', ...eur}
          ])
        : this.prikazGrupa
        ? [
          {label: 'Grupa', value: 'Grupa'},
          {label: 'Artikala', value: 'Artikala'},
          {label: 'Sa stanjem', value: 'SaStanjem'},
          {label: this.nazivUlazVr, value: 'UlazVr', ...eur},
          ...(this.poProdajnoj ? [{label: this.nazivIzlazVr, value: 'IzlazVr', ...eur}] : []),
          ...(this.prikaziZaradu ? [{label: 'Izlazna dobavna vrijednost', value: 'NabIzlaza', ...eur}] : []),
          {label: this.nazivStanjeVr, value: 'VrijZalihe', ...eur},
          ...(this.poProdajnoj ? [] : [{label: this.nazivIzlazVr, value: 'IzlazVr', ...eur}]),
          ...(this.prikaziZaradu ? [
            {label: 'RUC (upisana nab.)', value: 'Zarada', ...eur},
            {label: 'RUC %', value: 'RucPosto', format: '0.00"%"'},
            {label: 'Nab. izlaza (prosj.)', value: 'NabIzlazaProsj', ...eur},
            {label: 'RUC (prosj. nab.)', value: 'ZaradaProsj', ...eur},
            {label: 'RUC % (prosj.)', value: 'RucPostoProsj', format: '0.00"%"'}
          ] : [])
        ]
        : [
        // isti redoslijed i nazivi kao na ekranu: prvo kolicine, pa vrijednosti
        {label: 'Šifra', value: 'Sifra'},
        {label: 'Naziv', value: 'Naziv'},
        {label: 'JM', value: 'JM'},
        {label: 'Grupa', value: 'Grupa'},
        {label: 'Poč. stanje', value: 'PocKol', ...kol},
        {label: 'Ulaz kol.', value: 'UlazKol', ...kol},
        {label: 'Izlaz kol.', value: 'IzlazKol', ...kol},
        {label: 'Stanje', value: 'Stanje', ...kol},
        {label: this.nazivUlazVr, value: 'UlazVr', ...eur},
        ...(this.poProdajnoj ? [{label: this.nazivIzlazVr, value: 'IzlazVr', ...eur}] : []),
        // S RUC-om odlazi i nabavna vrijednost izlaza - obje se citaju zajedno
        ...(this.prikaziZaradu ? [{label: 'Izlazna dobavna vrijednost', value: 'NabIzlaza', ...eur}] : []),
        {label: this.nazivStanjeVr, value: 'VrijZalihe', ...eur},
        ...(this.poProdajnoj ? [] : [{label: this.nazivIzlazVr, value: 'IzlazVr', ...eur}]),
        {label: this.nazivProsjCijena, value: 'ProsjNC', ...eur},
        ...(this.prikaziZaradu ? [
          {label: 'RUC (upisana nab.)', value: 'Zarada', ...eur},
          {label: 'RUC %', value: 'RucPosto', format: '0.00"%"'},
          {label: 'Nab. izlaza (prosj.)', value: 'NabIzlazaProsj', ...eur},
          {label: 'RUC (prosj. nab.)', value: 'ZaradaProsj', ...eur},
          {label: 'RUC % (prosj.)', value: 'RucPostoProsj', format: '0.00"%"'}
        ] : [])
      ]

      const dbName = comp.CompanyNameDatabase ? comp.CompanyNameDatabase : ''
      xlsx([{sheet: this.prikazGrupa ? 'Grupe' : 'Skladiste', columns, content: lines}], {
        fileName: dbName + (this.prikazGrupa ? '_SkladisteGrupe_' : '_StanjeSkladista_') +
          dayjs().format('DD_MM_YYYY_HH_mm'),
        extraLength: 3,
        writeOptions: {}
      })
    }
  }
}
</script>

<style scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
/* One point up from what Vuetify gives a dense table: 14px body, 12px header.
   !important is needed because Vuetify's own rule
   (.v-data-table > .v-data-table__wrapper > table > tbody > tr > td) is more
   specific than anything a scoped class can reach. */
.stock-table >>> tbody td {
  font-size: 15px !important;
}
.stock-table >>> thead th {
  font-size: 13px !important;
}
input[type="date"] {
  border: 1px solid #bdbdbd;
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 13px;
}
</style>
